import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRestaurantBySlug } from '../lib/hooks';
import { supabase } from '../lib/supabase';
import { Table } from '../lib/types';
import { useTableOrder } from '../lib/table-context';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Utensils, QrCode, AlertTriangle } from 'lucide-react';
export function TablePage() {
  const {
    slug,
    tableNumber,
    token
  } = useParams<{
    slug: string;
    tableNumber: string;
    token: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    setTableOrder
  } = useTableOrder();
  const [table, setTable] = useState<Table | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'invalid_qr' | 'inactive' | null>(null);
  useEffect(() => {
    if (!restaurant || !tableNumber) return;
    async function fetchTable() {
      try {
        setLoading(true);
        setError(null);
        const {
          data,
          error: fetchError
        } = await supabase.from('tables').select('*').eq('restaurant_id', restaurant!.id).eq('table_number', tableNumber!).single();
        if (fetchError || !data) {
          setError('not_found');
          return;
        }
        // Check if table is active
        if (!data.is_active) {
          setError('inactive');
          return;
        }
        // If token is provided, validate it
        if (token && data.qr_token && data.qr_token !== token) {
          setError('invalid_qr');
          return;
        }
        setTable(data);
        // Set table order context for QR flow
        setTableOrder({
          tableId: data.id,
          tableNumber: data.table_number,
          tableName: data.table_name || `Mesa ${data.table_number}`,
          restaurantSlug: restaurant!.slug,
          restaurantId: restaurant!.id,
          orderChannel: token ? 'qr' : 'qr' // Both paths are self-service
        });
        // Also keep legacy localStorage for backward compat
        localStorage.setItem('noiza_current_table', data.id);
      } catch (err) {
        console.error('Error fetching table:', err);
        setError('not_found');
      } finally {
        setLoading(false);
      }
    }
    fetchTable();
  }, [restaurant, tableNumber, token, setTableOrder]);
  if (restLoading || loading) {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-5299">
        <Skeleton className="w-full max-w-md h-64 rounded-2xl" data-id="element-5300" />
      </div>;
  }
  // Error states
  if (error === 'invalid_qr') {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-5301">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden text-center p-8" data-id="element-5302">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5303">
            <AlertTriangle className="w-8 h-8 text-red-500" data-id="element-5304" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5305">QR Inválido</h1>
          <p className="text-gray-600 mb-6" data-id="element-5306">
            Este código QR no es válido o ha expirado. Por favor, consulta con
            el personal del restaurante.
          </p>
          <Button variant="secondary" onClick={() => navigate('/')} data-id="element-5307">
            Volver al inicio
          </Button>
        </div>
      </div>;
  }
  if (error === 'inactive') {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-5308">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden text-center p-8" data-id="element-5309">
          <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5310">
            <AlertTriangle className="w-8 h-8 text-amber-500" data-id="element-5311" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5312">
            Mesa No Disponible
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-5313">
            Esta mesa no está disponible en este momento. Por favor, consulta
            con el personal.
          </p>
          <Button variant="secondary" onClick={() => navigate('/')} data-id="element-5314">
            Volver al inicio
          </Button>
        </div>
      </div>;
  }
  if (!restaurant || !table || error === 'not_found') {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-5315">
        <EmptyState icon={QrCode} title="Mesa no encontrada" description="Este código QR no parece ser válido o la mesa no existe." actionLabel="Ir al inicio" onAction={() => navigate('/')} data-id="element-5316" />
      </div>;
  }
  const primaryColor = restaurant.primary_color || '#F59E0B';
  return <div className="min-h-screen bg-brand-offwhite flex flex-col items-center justify-center p-4" data-id="element-5317">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden text-center" data-id="element-5318">
        <div className="h-32 w-full flex items-center justify-center" style={{
        backgroundColor: primaryColor
      }} data-id="element-5319">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-20 h-20 rounded-2xl shadow-lg object-cover bg-white" data-id="element-5320" /> : <div className="w-20 h-20 rounded-2xl bg-white shadow-lg flex items-center justify-center text-3xl font-bold" style={{
          color: primaryColor
        }} data-id="element-5321">
              {restaurant.name.charAt(0)}
            </div>}
        </div>

        <div className="p-8" data-id="element-5322">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5323">
            Bienvenido a {restaurant.name}
          </h1>

          <div className="inline-flex items-center justify-center gap-2 bg-emerald-50 px-4 py-2 rounded-full mb-2" data-id="element-5324">
            <Utensils className="w-4 h-4 text-emerald-600" data-id="element-5325" />
            <span className="font-semibold text-emerald-700" data-id="element-5326">
              {table.table_name || `Mesa ${table.table_number}`}
            </span>
          </div>

          <div className="inline-flex items-center justify-center gap-1.5 bg-emerald-50/50 px-3 py-1 rounded-full mb-6" data-id="element-5327">
            <QrCode className="w-3.5 h-3.5 text-emerald-500" data-id="element-5328" />
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-5329">
              Autoatención
            </span>
          </div>

          <p className="text-gray-600 mb-8" data-id="element-5330">
            Estás listo para ordenar desde tu mesa. Explora nuestro menú y tu
            pedido llegará directamente aquí.
          </p>

          <Button size="lg" fullWidth onClick={() => navigate(slug ? `/r/${slug}/menu` : '/menu')} style={{
          backgroundColor: primaryColor
        }} className="text-white shadow-lg" data-id="element-5331">
            Ver Menú y Ordenar
          </Button>
        </div>
      </div>
    </div>;
}